import { useEffect } from 'react';
import { NavLink } from 'react-router';

import { Brand } from '@/components/layout/brand';
import { Icon } from '@/components/ui';
import { visibleSections, type NavItem } from '@/components/layout/nav-items';
import { useSession } from '@/features/auth/api/auth.api';
import { useEscapeKey, usePresence } from '@/hooks';
import { cn } from '@/lib/cn';
import { useUiStore } from '@/stores/ui.store';

/**
 * The off-canvas menu for small screens, shared by both layout modes.
 *
 * Below `md` neither a fixed sidebar nor a row of tabs is usable, so both
 * collapse to this: full-height, generous touch targets, and dismissed by
 * tapping the scrim, pressing Escape, or following a link.
 */
export function MobileNav() {
  const { data: user } = useSession();
  const open = useUiStore((state) => state.mobileNavOpen);
  const setOpen = useUiStore((state) => state.setMobileNavOpen);

  const sections = visibleSections(user);

  const close = (): void => {
    setOpen(false);
  };

  /* Held on screen through the slide-out; a panel that unmounts on close has
     nothing left to animate. */
  const { mounted, entered } = usePresence(open);

  useEscapeKey(() => {
    setOpen(false);
  }, open);

  /* A menu open behind a scrolling page reads as broken, so the body is
     locked while it is up. */
  useEffect(() => {
    if (!open) {
      return;
    }

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!mounted) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 md:hidden">
      <button
        type="button"
        aria-label="Close navigation"
        className={cn(
          'absolute inset-0 bg-navy-950/50',
          'transition-opacity duration-panel',
          entered ? 'opacity-100 ease-enter' : 'opacity-0 ease-exit',
        )}
        onClick={() => {
          setOpen(false);
        }}
      />

      <nav
        aria-label="Main"
        className={cn(
          'nav-chrome-vertical relative flex h-full w-64 max-w-[82vw] flex-col border-r shadow-[2px_0_10px_rgba(0,0,0,0.35)]',
          /* Transform rather than `left`: it runs on the compositor, so it
             stays smooth on a phone that is already busy painting the page. */
          'transition-transform duration-panel will-change-transform',
          entered ? 'translate-x-0 ease-enter' : '-translate-x-full ease-exit',
        )}
      >
        <div className="flex h-16 items-center justify-between gap-2 border-b border-nav-border px-3">
          <Brand />

          <button
            type="button"
            onClick={() => {
              setOpen(false);
            }}
            aria-label="Close navigation"
            className="-mr-1 p-2 text-content-muted hover:text-content"
          >
            <Icon name="close" size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          {sections.map((section, index) => (
            <div key={section.title ?? `section-${index}`} className={index > 0 ? 'mt-3' : ''}>
              {section.title && (
                <p className="px-3 pb-1 text-2xs font-bold tracking-wide text-nav-section uppercase">
                  {section.title}
                </p>
              )}

              <ul>
                {/* Groups keep their names but not their disclosure: the
                    drawer is already a scrolling list, and a second tap to
                    reveal four links costs more than the space it saves -
                    while ten settings under one heading stop being
                    scannable. */}
                {section.items.map((entry) =>
                  entry.children ? (
                    <li key={entry.label}>
                      <p className="px-3 pt-2 pb-0.5 pl-10 text-2xs font-bold tracking-wide text-nav-section uppercase">
                        {entry.label}
                      </p>

                      <ul>
                        {entry.children.map((child) => (
                          <li key={child.to}>
                            <MobileLink item={child} onNavigate={close} nested />
                          </li>
                        ))}
                      </ul>
                    </li>
                  ) : (
                    <li key={entry.to}>
                      <MobileLink item={entry} onNavigate={close} />
                    </li>
                  ),
                )}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-nav-border px-3 py-2 text-2xs text-nav-section">
          v{__APP_VERSION__}
        </div>
      </nav>
    </div>
  );
}

/** One row in the drawer, sized for a thumb rather than a mouse. */
function MobileLink({
  item,
  onNavigate,
  nested = false,
}: {
  item: NavItem;
  onNavigate: () => void;
  nested?: boolean;
}) {
  return (
    <NavLink
      to={item.to ?? '/'}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          /* 44px tall: a comfortable touch target, unlike the 28px rows the
             desktop layout uses. */
          'nav-item flex min-h-11 items-center gap-2.5 border-l-2 px-3 text-sm transition-colors',
          nested && 'pl-10',
          isActive ? 'nav-item-active border-azure-400 font-bold' : 'border-transparent',
        )
      }
    >
      <Icon name={item.icon} size={16} />
      {item.label}
    </NavLink>
  );
}
